import React, { useContext, useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { HamburgerMenuIcon, MultiplayerWhiteIcon, NutIcon } from '../../icons';
import { AlertCard, Dropdown, Text } from '../../common';
import { displayUser, isBetaUser } from '@repo-sapiens/helpers';
import { UserContext } from '../../../context';
import { css } from '@emotion/css';
import { E_Route } from '@repo-sapiens/constants';
import {
  Body,
  Container,
  Header,
  Humburger,
  LogoContailer,
  Nav,
} from './styles';
import { NAV_ITEMS } from './nav';
import { LogoutButton, NavItem, UserProfile } from './components';
import { useDemoMode, useSubscriptionPlan } from '../../../hooks';
import { theme } from '@repo-sapiens/theme';
import { T_User } from '@repo-sapiens/data-access-models';
import { MAX_CREDIT } from '../../../utils/constants';
import { checkOnboarding } from '../../../services/onboarding';
import { OnboardingDialog } from '../OnboardingModal';

let interval: NodeJS.Timeout | null = null;

export const PageTemplate: React.FC = () => {
  const { state: userState } = useContext(UserContext);
  const { demoMode, deactivateDemoMode } = useDemoMode();
  const { creditBalance, isFree, vipUser } = useSubscriptionPlan();
  const [onboardingNeeded, setOnboardingNeeded] = useState(false);

  useEffect(() => {
    interval = setInterval(() => {
      const onboardingCheck = checkOnboarding();
      setOnboardingNeeded(onboardingCheck);
      if (!onboardingCheck) {
        if (interval) {
          clearInterval(interval);
          interval = null;
        }
      }
    }, 1000); // Refresh every second
  }, [userState?._id]);

  return (
    <Container>
      <OnboardingDialog />

      <Header>
        <Nav>
          <div>
            <LogoContailer href={E_Route.Home}>
              <MultiplayerWhiteIcon style={{ width: '30px', height: '24px' }} />
            </LogoContailer>

            <div
              className={css`
                @media (max-width: 768px) {
                  display: none;
                }
              `}
            >
              {NAV_ITEMS.map((item) => (
                <NavItem
                  key={item.route}
                  route={item.route}
                  Icon={item.Icon}
                  label={item.label}
                  featureFlag={item.featureFlag && !isBetaUser(userState?._id)}
                  disabled={
                    onboardingNeeded ||
                    (isFree && item.freeDisabled && !vipUser)
                  }
                  active={
                    window.location.pathname?.split('/')[1]
                      ? item.route.includes(
                          window.location.pathname?.split('/')[1]
                        )
                      : item.route === window.location.pathname
                  }
                />
              ))}
            </div>
          </div>

          {!onboardingNeeded && (
            <Humburger>
              <Dropdown.Root
                noShadow
                triggerComponent={
                  <div>
                    <HamburgerMenuIcon
                      style={{ width: '20px', height: '20px' }}
                      fill="primary"
                    />
                  </div>
                }
              >
                <div
                  style={{
                    paddingInline: '18px',
                    backgroundColor: theme.colors.background.secondary,
                  }}
                >
                  {NAV_ITEMS.map((item) => (
                    <NavItem
                      key={item.route}
                      route={item.route}
                      Icon={item.Icon}
                      label={item.label}
                      featureFlag={
                        item.featureFlag && !isBetaUser(userState?._id)
                      }
                      disabled={isFree && item.freeDisabled && !vipUser}
                      mobile
                      active={
                        window.location.pathname?.split('/')[1]
                          ? item.route.includes(
                              window.location.pathname?.split('/')[1]
                            )
                          : item.route === window.location.pathname
                      }
                    />
                  ))}
                  <NavItem
                    route={E_Route.Settings}
                    label="Settings"
                    mobile
                    Icon={NutIcon}
                    active={window.location.pathname.includes(E_Route.Settings)}
                  />
                </div>
                <div
                  className={css`
                    width: 179px;
                    display: flex;
                    flex-direction: column;
                    align-items: flex-start;
                    padding-inline: 18px;
                  `}
                >
                  <LogoutButton />
                </div>
              </Dropdown.Root>
            </Humburger>
          )}

          <div
            className={css`
              @media (max-width: 768px) {
                display: none;
              }
            `}
          >
            {!demoMode && !onboardingNeeded && (
              <NavItem
                route={E_Route.Settings}
                label="Settings"
                Icon={NutIcon}
                active={window.location.pathname.includes(E_Route.Settings)}
              />
            )}
            <Dropdown.Root
              triggerComponent={
                <UserProfile
                  userName={displayUser(userState as T_User) || undefined}
                  profileUrl={userState?.profileUrl || undefined}
                />
              }
            >
              {demoMode && (
                <div
                  className={css`
                    width: 179px;
                    display: flex;
                    flex-direction: column;
                    align-items: flex-start;
                    padding: 8px 18px;
                    cursor: pointer;
                    margin-bottom: 8px;
                  `}
                  onClick={() => deactivateDemoMode()}
                >
                  <Text.BodySM color="highlight/1">Leave demo mode</Text.BodySM>
                </div>
              )}
              <div
                className={css`
                  width: 179px;
                  display: flex;
                  flex-direction: column;
                  align-items: flex-start;
                  padding-inline: 18px;
                `}
              >
                <LogoutButton />
              </div>
            </Dropdown.Root>
          </div>
        </Nav>
      </Header>

      <Body>
        {demoMode && (
          <div
            className={css`
              width: 100%;
              max-width: 688px;
              margin: auto;
              margin-bottom: 24px;
            `}
          >
            <AlertCard
              variant="info"
              text="You are currently in demo mode"
              actionText="Leave demo mode"
              onAction={() => deactivateDemoMode()}
            />
          </div>
        )}
        {userState?.rateLimitExceeded && (
          <div
            className={css`
              width: 100%;
              max-width: 688px;
              margin: auto;
              margin-bottom: 24px;
              margin-top: 24px;
            `}
          >
            <AlertCard
              variant="error"
              text="You may have noticed an interruption in your service. We have identified the issue with our providers, and a fix will be deployed within the next 24 hours."
            />
          </div>
        )}
        {(creditBalance || 0) > 270 && (creditBalance || 0) < MAX_CREDIT && (
          <div
            className={css`
              width: 100%;
              max-width: 688px;
              margin: auto;
              margin-bottom: 24px;
              margin-top: 24px;
            `}
          >
            <AlertCard
              variant="warning"
              text="You are running low on credits. Please upgrade your account to avoid service interruption."
            />
          </div>
        )}
        {creditBalance !== undefined && creditBalance >= MAX_CREDIT && (
          <div
            className={css`
              width: 100%;
              max-width: 688px;
              margin: auto;
              margin-bottom: 24px;
              margin-top: 24px;
            `}
          >
            <AlertCard
              variant="error"
              text="You have run out of credits. Please upgrade your account to avoid service interruption."
            />
          </div>
        )}
        <Outlet />
      </Body>
    </Container>
  );
};
